import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FaCode,
  FaServer,
  FaMobileAlt,
  FaDatabase,
  FaExchangeAlt,
  FaCloud,
  FaCheck,
  FaArrowRight,
} from "react-icons/fa";

import Header from "../Templete/Header";
import Footer from "../Templete/Footer";
import "./Technology.css";

const technologyGroups = [
  {
    title: "Frontend Development",
    label: "CUSTOMER & BUSINESS INTERFACES",
    icon: FaCode,
    description:
      "We build interfaces that make applications understandable and easy to navigate, with layouts that adapt to desktop, tablet, and mobile screens.",
    technologies: ["React JS", "JavaScript", "HTML5", "CSS3", "Angular"],
    applications: [
      "Business websites and customer portals",
      "Administrative and operational dashboards",
      "Responsive web applications",
    ],
  },
  {
    title: "Backend Development",
    label: "APPLICATION LOGIC & SERVICES",
    icon: FaServer,
    description:
      "Backend systems connect user actions with business rules, data, and external services. We structure applications around the workflows and integration requirements of each project.",
    technologies: ["Node.js", "Express.js", "PHP", "Java", "Python"],
    applications: [
      "Business logic and workflow processing",
      "REST API development",
      "Third-party service integrations",
    ],
  },
  {
    title: "Mobile Applications",
    label: "EXPERIENCES ON THE GO",
    icon: FaMobileAlt,
    description:
      "We develop mobile applications around the tasks users need to complete, bringing together clear navigation, service integrations, and device-specific requirements.",
    technologies: ["Flutter", "React Native", "Android", "Java", "Kotlin"],
    applications: [
      "Customer and partner applications",
      "Mobile service delivery platforms",
      "Connected web and mobile experiences",
    ],
  },
  {
    title: "Database Development",
    label: "STRUCTURED BUSINESS INFORMATION",
    icon: FaDatabase,
    description:
      "We design data structures around how information is created, accessed, and updated. Database work considers reporting needs, query behaviour, and application growth.",
    technologies: ["MySQL", "MongoDB", "SQL", "Data Modelling"],
    applications: [
      "Transactional and application data",
      "Reporting and data retrieval",
      "Query optimisation and indexing",
    ],
  },
  {
    title: "APIs & Integrations",
    label: "CONNECTED APPLICATIONS",
    icon: FaExchangeAlt,
    description:
      "We connect applications with external services through defined API contracts, helping teams manage requests, responses, transaction updates, and integration errors.",
    technologies: ["REST APIs", "JSON", "Webhooks", "Service Integrations"],
    applications: [
      "Payment and digital service integrations",
      "Webhook and status update workflows",
      "Partner-facing API infrastructure",
    ],
  },
  {
    title: "Deployment & Operations",
    label: "FROM DEVELOPMENT TO RELEASE",
    icon: FaCloud,
    description:
      "Deployment planning brings the application into its working environment. We consider configuration, web server setup, release steps, and operational visibility.",
    technologies: ["Linux", "Nginx", "Git", "SSL / TLS"],
    applications: [
      "Application deployment and configuration",
      "Version control and release preparation",
      "Logging and operational troubleshooting",
    ],
  },
];

const practices = [
  {
    number: "01",
    title: "Clear architecture",
    description:
      "Define how interfaces, application services, databases, and integrations work together before implementation expands.",
  },
  {
    number: "02",
    title: "Maintainable code",
    description:
      "Use consistent structure and separate responsibilities so future changes are easier to understand and implement.",
  },
  {
    number: "03",
    title: "Validation & access controls",
    description:
      "Consider input validation, permissions, and credential handling according to the application’s requirements.",
  },
  {
    number: "04",
    title: "Testing & release planning",
    description:
      "Check important user journeys and integration behaviour, then prepare deployment and maintenance requirements.",
  },
];

const selectionFactors = [
  {
    title: "Business requirements",
    description:
      "What the application needs to achieve and which workflows it must support.",
  },
  {
    title: "Users & devices",
    description:
      "How customers, partners, and internal teams will access and use the application.",
  },
  {
    title: "Existing systems",
    description:
      "The databases, services, and external integrations the solution needs to connect with.",
  },
  {
    title: "Long-term maintenance",
    description:
      "The skills, operating environment, and future changes needed to maintain the solution.",
  },
];

const Technology = () => {
  useEffect(() => {
    document.title =
      "Our Technology & Development Capabilities | Formax IT Solutions";
  }, []);

  return (
    <>
      <Header />

      <main className="fit-tech">
        {/* Hero */}
        <section className="fit-tech__hero">
          <div className="fit-tech__container">
            <nav className="fit-tech__breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">Technology</span>
            </nav>

            <div className="fit-tech__hero-grid">
              <div>
                <span className="fit-tech__eyebrow">OUR TECHNOLOGY</span>

                <h1>
                  The right technology.
                  <span>For the work that matters.</span>
                </h1>

                <p>
                  We bring together web, mobile, backend, and integration
                  technologies to build practical digital solutions around your
                  business.
                </p>

                <a href="#technology-stack" className="fit-tech__button">
                  Explore our capabilities
                  <FaArrowRight aria-hidden="true" />
                </a>
              </div>

              <aside
                className="fit-tech__overview"
                aria-label="Our technology capabilities"
              >
                <span className="fit-tech__eyebrow">
                  A CONNECTED DEVELOPMENT APPROACH
                </span>
                <h2>From interface to infrastructure.</h2>
                <p>
                  Each part of the application has a role. We consider how those
                  parts work together throughout development.
                </p>

                <div className="fit-tech__overview-grid">
                  <div>
                    <FaCode aria-hidden="true" />
                    <span>Web</span>
                  </div>
                  <div>
                    <FaMobileAlt aria-hidden="true" />
                    <span>Mobile</span>
                  </div>
                  <div>
                    <FaServer aria-hidden="true" />
                    <span>Backend</span>
                  </div>
                  <div>
                    <FaExchangeAlt aria-hidden="true" />
                    <span>Integrations</span>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Stack */}
        <section
          id="technology-stack"
          className="fit-tech__section"
          aria-labelledby="technology-stack-title"
        >
          <div className="fit-tech__container">
            <div className="fit-tech__heading">
              <span className="fit-tech__eyebrow">
                OUR DEVELOPMENT CAPABILITIES
              </span>
              <h2 id="technology-stack-title">
                A toolkit shaped by your requirements.
              </h2>
              <p>
                We select the stack according to the application, its users, and
                the systems it needs to connect with.
              </p>
            </div>

            <div className="fit-tech__stack-grid">
              {technologyGroups.map((group) => {
                const Icon = group.icon;

                return (
                  <article className="fit-tech__stack-card" key={group.title}>
                    <span className="fit-tech__icon">
                      <Icon aria-hidden="true" />
                    </span>

                    <span className="fit-tech__card-label">{group.label}</span>
                    <h3>{group.title}</h3>
                    <p>{group.description}</p>

                    <ul
                      className="fit-tech__tags"
                      aria-label={`${group.title} technologies`}
                    >
                      {group.technologies.map((technology) => (
                        <li key={technology}>{technology}</li>
                      ))}
                    </ul>

                    <div className="fit-tech__applications">
                      <h4>Where we use it</h4>
                      <ul>
                        {group.applications.map((application) => (
                          <li key={application}>
                            <FaCheck aria-hidden="true" />
                            <span>{application}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* Integration focus */}
        <section
          className="fit-tech__section fit-tech__section--dark"
          aria-labelledby="technology-integration-title"
        >
          <div className="fit-tech__container fit-tech__integration-grid">
            <div className="fit-tech__heading">
              <span className="fit-tech__eyebrow">
                PAYMENT & SERVICE TECHNOLOGY
              </span>
              <h2 id="technology-integration-title">
                Connecting systems is part of the solution.
              </h2>
              <p>
                Our work on payment technology and digital services involves
                more than displaying an API response. We consider how requests,
                updates, and exceptions fit into the complete business workflow.
              </p>
              <p>
                Through Formax Pay, we are building payment API infrastructure
                and orchestration capabilities for business partners.
              </p>
            </div>

            <div className="fit-tech__integration-list">
              <article>
                <h3>API workflows</h3>
                <p>
                  Structure requests, validate responses, and map external
                  services to application requirements.
                </p>
              </article>
              <article>
                <h3>Transaction updates</h3>
                <p>
                  Handle webhook events and status checks according to each
                  partner’s integration contract.
                </p>
              </article>
              <article>
                <h3>Operational visibility</h3>
                <p>
                  Build reporting and diagnostic tools that help teams
                  investigate activity and resolve exceptions.
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Engineering practices */}
        <section
          className="fit-tech__section"
          aria-labelledby="technology-practices-title"
        >
          <div className="fit-tech__container">
            <div className="fit-tech__heading">
              <span className="fit-tech__eyebrow">HOW WE BUILD</span>
              <h2 id="technology-practices-title">
                Thoughtful implementation at every stage.
              </h2>
              <p>
                Our approach considers the working application, the people
                maintaining it, and the changes it may need over time.
              </p>
            </div>

            <div className="fit-tech__practice-grid">
              {practices.map((practice) => (
                <article key={practice.number} className="fit-tech__practice">
                  <span aria-hidden="true">{practice.number}</span>
                  <h3>{practice.title}</h3>
                  <p>{practice.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Technology decisions */}
        <section
          className="fit-tech__section fit-tech__section--soft"
          aria-labelledby="technology-selection-title"
        >
          <div className="fit-tech__container fit-tech__selection-grid">
            <div className="fit-tech__heading">
              <span className="fit-tech__eyebrow">TECHNOLOGY DECISIONS</span>
              <h2 id="technology-selection-title">
                Start with the problem, then choose the tools.
              </h2>
              <p>
                A technology choice should support the project’s goals. We
                consider the practical requirements before proposing a
                development approach.
              </p>
            </div>

            <div className="fit-tech__factors">
              {selectionFactors.map((factor) => (
                <article key={factor.title}>
                  <h3>{factor.title}</h3>
                  <p>{factor.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="fit-tech__section">
          <div className="fit-tech__container">
            <div className="fit-tech__cta">
              <div>
                <span className="fit-tech__eyebrow">LET’S CONNECT</span>
                <h2>Have a technical requirement in mind?</h2>
                <p>
                  Tell us about your application, integration, or existing
                  system. We can help outline the development requirements.
                </p>
              </div>

              <Link to="/contact-us" className="fit-tech__button">
                Contact us
                <FaArrowRight aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Technology;
